import { useRoute } from '#imports'
import { computed } from 'vue'
import type { LocationQueryValue, RouteParamValue } from 'vue-router'

import type { Grade } from '@backmarket/http-api/src/api-models/Grade'

import { useMasterPP } from '~/scopes/product-core/composables/useMasterPP/useMasterPP'

export type UrlParams = Readonly<{
  productId: string
  slug: string
  grade?: Grade['value']
  offerType?: 7
  mobilePlan?: string
  mobilePlanOfferExperiment?: string
}>

const fromQueryParam = (
  param: LocationQueryValue | LocationQueryValue[],
): string | null => (Array.isArray(param) ? param[0] : (param ?? null))

const fromQueryParamAsNumber = (
  param: LocationQueryValue | LocationQueryValue[],
): number | null => {
  const p = fromQueryParam(param)

  return p ? parseInt(p, 10) : null
}

const fromRouteParam = (
  param?: RouteParamValue | RouteParamValue[],
): string | null => (Array.isArray(param) ? param[0] : (param ?? null))

const fromQueryParamAsBoolean = (
  param: LocationQueryValue | LocationQueryValue[],
): boolean | null => {
  const p = fromQueryParam(param)

  return p ? p === 'true' : null
}

export const useUrlParams = (route = useRoute()) => {
  const uuid = fromRouteParam(route.params?.uuid)
  const slug = fromRouteParam(route.params?.slugV2)
  const {
    isMasterPPUrl,
    masterPPProductId,
    masterPPGrade,
    masterPPSpecialOfferType,
  } = useMasterPP(route)

  const grade = computed(() =>
    isMasterPPUrl.value
      ? masterPPGrade.value
      : fromQueryParamAsNumber(route.query?.l),
  )
  const offer = computed(() =>
    isMasterPPUrl.value
      ? masterPPSpecialOfferType.value
      : fromQueryParamAsNumber(route.query?.offerType),
  )
  const mobilePlan = computed(() =>
    fromQueryParam(route.query?.mobilePlanOfferId),
  )

  const variantClicked = computed(() => route.query?.variantClicked)

  const productId = isMasterPPUrl.value ? masterPPProductId.value : uuid

  const mobilePlanOfferExperiment = computed(() =>
    fromQueryParam(route.query?.mobile_plan_offer_experiment),
  )
  const closestPickerConfig = computed(() =>
    fromQueryParamAsBoolean(route.query?.closestPickerConfig),
  )

  return {
    productId,
    slug,
    grade,
    offerType: offer,
    mobilePlan,
    variantClicked,
    mobilePlanOfferExperiment,
    closestPickerConfig,
  }
}
